import {
  ArrowBack,
  ChevronLeft,
  ChevronRight,
  Close,
  Update,
  Add,
} from '@mui/icons-material'
import {
  CircularProgress,
  Skeleton,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import { useParams } from 'react-router-dom'
import { ExpandMore } from '@mui/icons-material'
import { QrCode2 } from '@mui/icons-material'
import dayjs from 'lib/dayjs'

import ConfirmChangesModal from 'views/DigitalTrayMapping/AssignedDigitalTrays/TrayVisualization/Modals/ConfirmChangesModal/ConfirmChangesModal'
import { LoadingButton } from 'components/mui'
import ViewImageDialog from 'components/organisms/ViewImageDialog/ViewImageDialog'

import useEditTray from './EditTray.logic'
import TrayMapWithControls from './TrayMapWithControls/TrayMapWithControls'
import UDIScannerModal from 'components/organisms/UDIScannerModal/UDIScannerModal'

import './EditTray.scss'
import { ConfirmLoadMultipleScrewsModal } from 'components/organisms/ConfirmLoadMultipleScrewsModal/ConfirmLoadMultipleScrewsModal'
import { BackOrButton } from 'components/BackOrButton'
import TrayProduct from 'views/DigitalTrayMapping/AssignedDigitalTrays/TrayProducts/TrayProduct/TrayProduct'
import { LoadMultipleScrewsBox } from 'components/organisms/LoadMultipleScrewsBox/LoadMultipleScrewsBox'
import ImageVerificationModal from 'components/organisms/TrayProcedureEditMode/ImageVerificationModal/ImageVerificationModal'
import OtherTrayItemsContextProvider from 'components/organisms/OtherTrayItems/OtherTrayItemsContextProvider'
import { OtherItemsTriggerButton } from 'components/organisms/OtherTrayItems/OtherTrayItemsTriggerButton'
import CustomImplantModal from 'components/organisms/CustomImplantModal/CustomImplantModal'
import { TransformWrapper } from 'react-zoom-pan-pinch'

const EditTray = ({ isReviewing }: { isReviewing: boolean }) => {
  const {
    isInEditMode,
    handleEditModeChange,
    loading,
    error,
    subTrayItem,
    trayDetails,
    isConfirmationOpen,
    handleConfirmationClick,
    handleLoadWithUDIsClick,
    handleLoadWithDIClick,
    isLoadingWithUDIs,
    isAdditionalDeviceCountModalOpen,
    isRejecting,
    handleUserConfirmation,
    handleConfirmationClose,
    isUpdating,
    renderSubTrayPlates,
    renderNewlyLoadedScrews,
    newlyLoadedScrews,
    trayPlates,
    isLoadMultipleBoxVisible,
    isInstrumentTray,
    isCustomImplantTray,
    hasPlates,
    loadAllScrews,
    increaseAllPlatesBy,
    isAllScrewsLoaded,
    isAllPlatesLoaded,
    hasScrews,
    totalPlateCount,
    totalScrews,
    imageScale,
    imageRotation,
    isLegendOpen,
    setIsLegendOpen,
    // Image history related
    currentImageIndex,
    imageHistory,
    isViewingHistory,
    hasMultipleImages,
    goToPreviousImage,
    goToNextImage,
    goToLatestImage,
    trayImage,
    isImageLoading,
    formattedTimestamp,
    referenceImageTitle,
    isImagePreviewOpen,
    setIsImagePreviewOpen,
    isReferenceImageAccurateModalOpen,
    setIsReferenceImageAccurateModalOpen,
    handleReferenceImageAccurate,
    handleCaptureNewReferenceImage,
    otherItems,
    setOtherItems,
    isCustomImplantModalOpen,
    handleAddCustomImplantTrigger,
    handleUDIScan,
    handleTrayStateUpdate,
    isUDIScannerModalOpen,
    searchProductResult,
    isSearchingProduct,
    searchProductError,
    handleUDIScannerModalClose,
    isQuantityExceededModalOpen,
    quantityValidationData,
    handleQuantityExceededCancel,
  } = useEditTray()
  const { trayId } = useParams<{ trayId: string }>()

  return (
    <OtherTrayItemsContextProvider
      otherItems={otherItems}
      setOtherItems={setOtherItems}
    >
      <div className="tray-procedure-edit-mode-organism_wrapper edit-tray-wrapper">
        <LoadMultipleScrewsBox
          open={isLoadMultipleBoxVisible}
          trayType={trayDetails?.trayType || 'stryker screw caddy'}
        />

        <div className="header contained-desktop">
          <div className="header_actions">
            <BackOrButton
              variant="text"
              className="back_button"
              sx={{ width: 'auto' }}
              size="small"
              fallbackUrl={`/tray-management/${trayId}`}
              fallbackChildren={
                <>
                  <ArrowBack sx={{ mr: 0.5 }} /> BACK
                </>
              }
            >
              <ArrowBack sx={{ mr: 0.5 }} /> BACK
            </BackOrButton>
          </div>

          <Typography variant="h1">Sub-Tray Details</Typography>
        </div>
        {!loading && !error && subTrayItem && (
          <div className="content-container">
            <div className="reference-panel">
              <div className="panel-header">
                <Typography variant="h6">{referenceImageTitle}</Typography>
                {formattedTimestamp && (
                  <Typography variant="caption" className="timestamp">
                    {formattedTimestamp} | Sub-Tray ID:{' '}
                    <strong>{subTrayItem?.id}</strong> | Tray Type:{' '}
                    <strong>{trayDetails?.trayType}</strong>
                  </Typography>
                )}
              </div>

              <div className="reference-image-container">
                {!subTrayItem || loading || error || isImageLoading ? (
                  <Skeleton
                    variant="rounded"
                    animation="wave"
                    className="skeleton"
                  />
                ) : (
                  <>
                    <img
                      src={trayImage}
                      alt={subTrayItem.description}
                      className="reference-image"
                      style={{
                        transform: `scale(${imageScale}) rotate(${imageRotation}deg)`,
                      }}
                      onClick={() => setIsImagePreviewOpen(true)}
                    />

                    {hasMultipleImages && (
                      <div className="image-navigation-controls">
                        <button
                          onClick={goToPreviousImage}
                          disabled={
                            currentImageIndex >= imageHistory.length - 1
                          }
                          className="nav-button prev-button"
                          aria-label="Previous image"
                        >
                          <ChevronLeft />
                        </button>

                        <div className="image-counter">
                          {imageHistory.length - currentImageIndex} /{' '}
                          {imageHistory.length}
                        </div>

                        <button
                          onClick={goToNextImage}
                          disabled={currentImageIndex <= 0}
                          className="nav-button next-button"
                          aria-label="Next image"
                        >
                          <ChevronRight />
                        </button>
                      </div>
                    )}

                    {isViewingHistory && (
                      <Tooltip title="Go to latest image">
                        <button
                          onClick={goToLatestImage}
                          className="latest-image-button"
                          aria-label="Go to latest image"
                        >
                          <Update /> <span>Return To Latest</span>
                        </button>
                      </Tooltip>
                    )}
                  </>
                )}
              </div>
            </div>

            {!isInstrumentTray && (
              <div className="map-container">
                <TransformWrapper
                  initialScale={1}
                  initialPositionX={0}
                  initialPositionY={0}
                  pinch={{
                    step: 10,
                  }}
                  wheel={{
                    step: 10,
                  }}
                  smooth
                  doubleClick={{
                    disabled: true,
                  }}
                >
                  <TrayMapWithControls
                    isInEditMode={isInEditMode}
                    isLoadingWithUDIs={isLoadingWithUDIs}
                    trayType={trayDetails?.trayType || 'stryker screw caddy'}
                    trayImg={trayDetails?.trayImg}
                    isLegendOpen={isLegendOpen}
                    setIsLegendOpen={setIsLegendOpen}
                  />
                </TransformWrapper>

                <div className="instruction-text">
                  <div className="instruction-header">
                    <div className="instruction-content">
                      <Typography variant="h6">
                        {isInEditMode
                          ? 'Edit Mode: Click on screws or plates to load them'
                          : 'View Mode: Click Edit to make changes'}
                      </Typography>
                    </div>
                    <Button
                      variant={isInEditMode ? 'contained' : 'outlined'}
                      onClick={handleEditModeChange}
                      className="mode-toggle-button"
                      disabled={isUpdating}
                      size="small"
                    >
                      {isInEditMode ? 'Exit Edit Mode' : 'Edit Tray'}
                    </Button>
                  </div>

                  {isInEditMode && (
                    <div className="instruction-actions">
                      <OtherItemsTriggerButton
                        buttonProps={{
                          className: 'load-other-items-button',
                        }}
                        trayType={
                          trayDetails?.trayType || 'stryker screw caddy'
                        }
                      />

                      {!isCustomImplantTray && (
                        <>
                          <Button
                            variant={
                              !isLoadingWithUDIs ? 'contained' : 'outlined'
                            }
                            onClick={handleLoadWithDIClick}
                            className="load-mode-button"
                            disabled={isUpdating}
                            size="small"
                          >
                            Load With DI
                          </Button>
                          <Button
                            variant={
                              isLoadingWithUDIs ? 'contained' : 'outlined'
                            }
                            onClick={handleLoadWithUDIsClick}
                            className="load-mode-button"
                            disabled={isUpdating}
                            size="small"
                          >
                            Load With UDI
                          </Button>
                        </>
                      )}

                      {isCustomImplantTray && (
                        <Button
                          variant="contained"
                          className="load-mode-button"
                          disabled={isUpdating}
                          size="small"
                          startIcon={<Add />}
                          onClick={handleAddCustomImplantTrigger}
                        >
                          Add Custom Products
                        </Button>
                      )}

                      <LoadingButton
                        variant="contained"
                        onClick={() => handleConfirmationClick()}
                        className="confirm-button"
                        loading={isUpdating}
                        size="small"
                      >
                        Confirm
                      </LoadingButton>
                    </div>
                  )}
                </div>

                {isInEditMode && (
                  <div
                    className={`mode-banner ${
                      isLoadingWithUDIs ? 'scan-mode' : 'click-mode'
                    }`}
                  >
                    <Typography variant="body1">
                      {isLoadingWithUDIs ? (
                        <>
                          <strong>Load With UDI:</strong> Select a screw or
                          plate location, then scan the asset to load it
                        </>
                      ) : (
                        <>
                          <strong>Load With DI:</strong> Click directly on a
                          screw or plate location to load an asset with
                          pre-indexed device identifiers
                        </>
                      )}
                    </Typography>
                  </div>
                )}

                {isInEditMode && !isCustomImplantTray && (
                  <div className="load-all-container">
                    {hasScrews && !isLoadingWithUDIs && (
                      <Button
                        disabled={isAllScrewsLoaded}
                        onClick={loadAllScrews}
                        variant="outlined"
                        className="load-all-button"
                      >
                        Load All Screws
                      </Button>
                    )}
                    {hasPlates && (
                      <Button
                        disabled={isAllPlatesLoaded}
                        onClick={() => increaseAllPlatesBy(1)}
                        variant="outlined"
                        className="load-all-button"
                      >
                        Increase All Plates
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {loading && <CircularProgress className="loading-spinner" />}

        {!loading && error && (
          <Typography className="error" variant="body1">
            {error.message}
          </Typography>
        )}

        <ConfirmChangesModal
          open={isConfirmationOpen}
          modalText={
            isRejecting
              ? 'Are you sure you want to reject this tray?'
              : isReviewing
              ? 'Are you sure you want to approve this tray'
              : undefined
          }
          onClose={handleConfirmationClose}
          handleConfirm={handleUserConfirmation}
          handleSaveChangesCancellation={handleConfirmationClose}
        />

        <ConfirmLoadMultipleScrewsModal
          open={isAdditionalDeviceCountModalOpen}
          onClose={() => {
            return
          }}
        />

        {!loading &&
          !error &&
          subTrayItem &&
          !isInstrumentTray &&
          !isCustomImplantTray &&
          (newlyLoadedScrews.length > 0 ||
            (trayPlates || []).some((p: any) => p.loadedViaUDI)) && (
            <div className="tray-contents-section contained-desktop">
              <Accordion defaultExpanded>
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  aria-controls="newly-loaded-screws-content"
                  id="newly-loaded-screws-header"
                >
                  <div className="accordion-summary">
                    <Typography variant="h2" style={{ color: '#2196F3' }}>
                      Recently Loaded Items
                    </Typography>
                    <Typography variant="body2">
                      {newlyLoadedScrews.length +
                        (trayPlates || []).filter((p: any) => p.loadedViaUDI)
                          .length}{' '}
                      items loaded via UDI scanning
                    </Typography>
                  </div>
                </AccordionSummary>
                <AccordionDetails>
                  <div className="tray-contents-items">
                    {renderNewlyLoadedScrews()}
                    {(trayPlates || [])
                      .filter((p: any) => p.loadedViaUDI)
                      .map((plate: any, index: number) => (
                        <div
                          key={`${plate.plateName}_${index}`}
                          className="sub-tray-plate_container newly-loaded-screw"
                        >
                          <QrCode2
                            className="qr-code"
                            style={{ color: '#2196F3' }}
                          />
                          <div className="content">
                            <Typography variant="subtitle2">
                              Plate: {plate.plateName}
                            </Typography>
                            {plate.deviceId && (
                              <Typography variant="subtitle2">
                                Device ID: {plate.deviceId}
                              </Typography>
                            )}
                            {plate.deviceDescription && (
                              <Typography variant="subtitle2">
                                Device Description: {plate.deviceDescription}
                              </Typography>
                            )}
                            <Typography variant="subtitle2">
                              Count: {plate.plateCount}
                            </Typography>
                            {plate.loadedAt && (
                              <Typography
                                variant="subtitle2"
                                style={{ color: '#2196F3', fontWeight: 'bold' }}
                              >
                                Loaded via UDI:{' '}
                                {dayjs(plate.loadedAt).format(
                                  'MMM D, YYYY h:mm A'
                                )}
                              </Typography>
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                </AccordionDetails>
              </Accordion>
            </div>
          )}

        {!loading &&
          !error &&
          subTrayItem &&
          !isInstrumentTray &&
          !isCustomImplantTray && (
            <div className="tray-contents-section contained-desktop">
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  aria-controls="tray-contents-content"
                  id="tray-contents-header"
                >
                  <div className="accordion-summary">
                    <Typography variant="h2">Tray Contents</Typography>
                    <Typography variant="body2">
                      {totalScrews} Screws, {totalPlateCount} Plates
                    </Typography>
                  </div>
                </AccordionSummary>
                <AccordionDetails>
                  <div className="tray-contents-items">
                    {renderSubTrayPlates()}

                    {trayDetails?.screws.length === 0 &&
                      trayDetails?.plates.length === 0 && (
                        <Typography variant="body2">
                          No screws or plates in this tray
                        </Typography>
                      )}

                    {trayDetails?.screws.map((screw, index) => (
                      <TrayProduct
                        key={`${screw.deviceId}${index}`}
                        {...screw}
                        showDispositionStatus={false}
                      />
                    ))}
                  </div>
                </AccordionDetails>
              </Accordion>
            </div>
          )}

        {isLegendOpen && (
          <div className="legend-overlay">
            <div className="legend-container">
              <div className="legend-header">
                <h3>Hardware Status Legend</h3>
                <button
                  className="close-legend-button"
                  onClick={() => setIsLegendOpen(false)}
                  aria-label="Close Legend"
                >
                  <Close />
                </button>
              </div>
              <div className="legend-items">
                <div className="legend-item">
                  <div className="legend-circle blue" />
                  <span>Loaded</span>
                </div>
                <div className="legend-item">
                  <div className="legend-circle orange" />
                  <span>Selected</span>
                </div>
                <div className="legend-item">
                  <div className="legend-circle wasted" />
                  <span>Wasted</span>
                </div>
                <div className="legend-item">
                  <div className="legend-circle not-loaded" />
                  <span>Not Loaded</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <CustomImplantModal
          open={isCustomImplantModalOpen}
          onClose={handleAddCustomImplantTrigger}
        />

        <ViewImageDialog
          open={isImagePreviewOpen}
          handleClose={() => setIsImagePreviewOpen(false)}
          trayImage={trayImage || ''}
        />

        <ImageVerificationModal
          open={isReferenceImageAccurateModalOpen}
          onClose={() => setIsReferenceImageAccurateModalOpen(false)}
          onConfirm={handleReferenceImageAccurate}
          onRecapture={handleCaptureNewReferenceImage}
        />

        <UDIScannerModal
          open={isUDIScannerModalOpen}
          onClose={handleUDIScannerModalClose}
          onScan={handleUDIScan}
          onTrayStateUpdate={handleTrayStateUpdate}
          searchResult={searchProductResult?.searchProductsByUdi}
          isSearching={isSearchingProduct}
          searchError={searchProductError}
          referenceImageUrl={trayImage}
          trayType={trayDetails?.trayType}
        />

        {/* Quantity Validation Modal */}
        <Dialog
          open={isQuantityExceededModalOpen}
          onClose={handleQuantityExceededCancel}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Typography variant="h6" color="error">
              Quantity Exceeded
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              The scanned product indicates a quantity of{' '}
              <strong>{quantityValidationData?.scannedQuantity}</strong>, but{' '}
              <strong>{quantityValidationData?.detectedQuantity}</strong> items
              were detected in the tray.
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Product: <strong>{quantityValidationData?.productName}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please verify the correct quantity and try again. Make sure only
              the expected number of items are loaded in the tray.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleQuantityExceededCancel}
              variant="outlined"
              color="primary"
            >
              OK
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </OtherTrayItemsContextProvider>
  )
}

export default EditTray
